import { ApolloError, useMutation } from '@apollo/client';
import { SAVE_USER_PREFERENCES } from '@bechtel/client-gql';
import {
  Box,
  ButtonAppearance,
  ButtonSize,
  FormControl,
  Radio,
  Select,
  SideSheetFooter,
  Spinner,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  ToastAction,
  ToastType,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import { SaveUserPreferencesError, SaveUserPreferencesInput, SaveUserPreferencesPayload } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { Formik, FormikHelpers as FormikActions } from 'formik';
import { find, get, map } from 'lodash';
import { FC, useState } from 'react';
import { ErrorBoundary } from '~/components/atoms';
import { DrawerFooterButtonStyled, ListSelectionStyled } from '~/components/ions';
import { useAppUser } from '~/components/molecules/appuser/appuser-context';
import { DisplaySettingsError } from '../error/display-setting-error';
import { Lang, languageList, Quantity, unitOfMeasureList } from '../form-data';
import { ButtonBox, MySettingsBoxStyled } from '../styled';
import { DrawerSubHeaderStyled } from './styled';

export const DropdownSelectedItem = ({ item }: { item: any }) => {
  return (
    <Text family="graphik" size="400" color="grey.1000">
      {item.name}
    </Text>
  );
};
export const DropdownItem = ({ item }: { item: any }) => {
  return (
    <Text size="400" color="grey.1000">
      {item.name}
    </Text>
  );
};

const userPrefsSavedToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Setting changed successfully.</Trans>
      </Text>
    </Box>
  );
};

interface formikInitialValues {
  unitOfMeasure: Quantity;
  language: Lang;
}

interface DisplaySettingsProps {
  close: () => void;
  updateUserPreferenceSettings: (language: Lang) => void;
}

export const DisplaySettings: FC<DisplaySettingsProps> = ({ close, updateUserPreferenceSettings }) => {
  const { user } = useAppUser();
  const showUserQuantityOption = user?.project?.projectConfiguration?.usersQuantityDisplayToggle;

  const userLanguage = find(languageList, (lang) => lang.languageValue === user?.userPreference?.language);
  const userUnitOfMeasure = find(
    unitOfMeasureList,
    (measureUnit) => measureUnit.unit === user?.userPreference?.unitOfMeasure
  );

  const initialValues: formikInitialValues = {
    unitOfMeasure: userUnitOfMeasure ? userUnitOfMeasure : unitOfMeasureList[0],
    language: userLanguage ? userLanguage : languageList[0],
  };
  const [saveUserPreferencesError, setSaveUserPreferencesError] = useState<
    SaveUserPreferencesError[] | ApolloError | null
  >(null);

  const { close: closeErrorModal } = useModalState(false);

  const dispatchToast = useToastsDispatch();

  const [saveUserPrefs, { loading }] = useMutation<
    { saveUserPreferences: SaveUserPreferencesPayload },
    { input: SaveUserPreferencesInput }
  >(SAVE_USER_PREFERENCES);

  const closeModal = () => {
    setSaveUserPreferencesError(null);
    closeErrorModal();
  };

  return (
    <>
      {saveUserPreferencesError && (
        <ErrorBoundary>
          <DisplaySettingsError close={closeModal} errors={saveUserPreferencesError} />
        </ErrorBoundary>
      )}
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={async (values: formikInitialValues, actions: FormikActions<formikInitialValues>) => {
          const { data } = await saveUserPrefs({
            variables: {
              input: {
                unitOfMeasure: values.unitOfMeasure.enumValue,
                language: values.language.languageValue,
              },
            },
          });

          const preferences = get(data, 'saveUserPreferences.user.userPreference');
          if (preferences) {
            const language = find(languageList, (lang) => lang.languageValue === preferences.language);
            if (language) {
              updateUserPreferenceSettings(language);
            }
            close();

            const toastAction: ToastAction = {
              type: 'ADD_TOAST',
              payload: {
                toastType: ToastType.INFO,
                content: userPrefsSavedToast,
                autoDismissTimeout: 5000,
              },
            };
            dispatchToast(toastAction);
            setSaveUserPreferencesError(null);
          } else {
            const userPrefErrors: SaveUserPreferencesError[] = get(data, 'saveUserPreferences.errors', []);
            if (userPrefErrors.length > 0) {
              setSaveUserPreferencesError(userPrefErrors);
            } else {
              setSaveUserPreferencesError([SaveUserPreferencesError.SAVING_USER_PREFERENCE_FAILED]);
            }
            actions.resetForm();
          }
        }}
      >
        {({ dirty, handleSubmit, values, setFieldValue }) => {
          return (
            <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }}>
              <MySettingsBoxStyled>
                {showUserQuantityOption && (
                  <Box flexDirection={{ sm: 'column' }}>
                    <DrawerSubHeaderStyled>Quantities</DrawerSubHeaderStyled>
                    <Box mt={{ sm: '600' }} mb={{ sm: '400' }} mx={{ sm: '500' }}>
                      <Text family="graphik" size="300" color="black">
                        <Trans>Select how would you like the work package scope to display</Trans>
                      </Text>
                    </Box>
                    <Box mx={{ sm: '500' }}>
                      <Text family="graphik" size="300" color="black">
                        <Trans>in your user interface</Trans>
                      </Text>
                    </Box>
                    {map(unitOfMeasureList, (quantity: Quantity) => {
                      const selected = quantity.unit === values.unitOfMeasure.unit;
                      return (
                        <ListSelectionStyled
                          style={{ borderBottom: '0px' }}
                          onClick={() => {
                            setFieldValue('unitOfMeasure', quantity);
                          }}
                          key={quantity.unit}
                          data-testid={`quantity-${quantity.id}`}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Radio size={TextFieldSize.LARGE} checked={selected} readOnly />
                          </Box>
                          <Text family="graphik" size="300" color="black">
                            <Trans>{quantity.name}</Trans>
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}
                  </Box>
                )}
                <DrawerSubHeaderStyled>Language</DrawerSubHeaderStyled>
                <Box
                  my={{ sm: '500' }}
                  mx={{ sm: '500' }}
                  flexGrow={{ sm: '1' }}
                  css={`
                    max-width: 352px;
                  `}
                >
                  <FormControl
                    label="Language (Required)"
                    css={`
                      flex-grow: 1;
                    `}
                  >
                    <Select
                      name="language"
                      size={TextFieldSize.LARGE}
                      appearance={TextFieldAppearance.NORMAL}
                      items={languageList}
                      selectedItem={values.language}
                      itemComponent={DropdownItem}
                      selectedComponent={DropdownSelectedItem}
                      onSelectedItemChange={(changes: any) => {
                        setFieldValue('language', changes.selectedItem);
                      }}
                      popperStyle={{ zIndex: 3 }}
                    />
                  </FormControl>
                </Box>
              </MySettingsBoxStyled>
              <SideSheetFooter>
                <ButtonBox>
                  <Box mr={{ sm: '400' }}>
                    <DrawerFooterButtonStyled
                      size={ButtonSize.MEDIUM}
                      appearance={ButtonAppearance.SECONDARY}
                      onClick={close}
                    >
                      &nbsp;<Trans>Cancel</Trans>&nbsp;
                    </DrawerFooterButtonStyled>
                  </Box>
                  <Box>
                    <DrawerFooterButtonStyled
                      type="submit"
                      size={ButtonSize.MEDIUM}
                      disabled={!dirty}
                      appearance={ButtonAppearance.SECONDARY}
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      {loading && <Spinner />}
                      {!loading && <Trans>Apply Changes</Trans>}
                    </DrawerFooterButtonStyled>
                  </Box>
                </ButtonBox>
              </SideSheetFooter>
            </Box>
          );
        }}
      </Formik>
    </>
  );
};
